import { useCallback } from 'react'
import { FetchResult } from '@apollo/client'
import { useLocalStorage } from '@uidotdev/usehooks'
import { googleLogout } from '@react-oauth/google'
import { useNavigate } from 'react-router-dom'
import { useDevCycleClient } from '@devcycle/react-client-sdk'
import { useLoginMutation, LoginResponse, Credentials } from 'gql/hooks/useLoginMutation'
import {
  useEmailLoginMutation,
  LoginEmailResponse,
  type EmailCredentials
} from '~/graphql/hooks/useEmailLoginMutation'
import { useUser, User } from '~/graphql/hooks/useUser'
import Mixpanel from '~/utilities/mixpanel'
import { toast } from 'sonner'
import { storage } from '~/utilities/storageUitls'
import { useAppLoadingStore } from '~/store/appLoadingStore'

interface Auth {
  login: (credentials: Credentials) => Promise<FetchResult<LoginResponse>>
  emailLogin: (
    emailCredentials: EmailCredentials
  ) => Promise<FetchResult<LoginEmailResponse>>
  logout: () => Promise<void>
  token: string | null
  setToken: (token: string | null) => void
  getUser: () => Promise<{ me: User | null } | null>
  loading: boolean
  getToken: () => string | null
}

export function useAuth(): Auth {
  const devcycleClient = useDevCycleClient()
  const navigate = useNavigate()
  const {
    refetchUserData,
    clearCache: clearUserCache,
    loading: userIsLoading
  } = useUser()
  const [token, setToken] = useLocalStorage<string | null>('access_token', null)
  const { setLoading: setAppLoading } = useAppLoadingStore()

  const {
    login,
    loading: loginIsLoading,
    client
  } = useLoginMutation({
    onCompleted(data: LoginResponse) {
      if (data.login?.data?.token) {
        setToken(data.login.data.token)
        Mixpanel.identify({
          id: data.login.data.user.id.toString(),
          data: {
            email: data.login.data.user.email,
            framework: 'react'
          }
        })

        Mixpanel.track({
          event: 'FE:login',
          data: {
            framework: 'react',
            source: 'email-password'
          }
        })

        void refetchUserData()
        window.location.assign('/org')
      }
      if (data.login?.error) {
        toast.error(data.login.error)
      }
    }
  })

  const { emailLogin, loading: emailLoginIsLoading } = useEmailLoginMutation({
    onCompleted(data: LoginEmailResponse) {
      if (data.emailLogin?.success && data.emailLogin.data?.token) {
        setToken(data.emailLogin.data.token)
        Mixpanel.identify({
          id: data.emailLogin.data.user.id.toString(),
          data: {
            email: data.emailLogin.data.user.email,
            framework: 'react'
          }
        })
        window.location.reload()
      }

      if (data.emailLogin?.success) {
        navigate('/?login=success')
      }

      if (data.emailLogin?.error) {
        toast.error(data.emailLogin?.error)
      }
    }
  })

  const getUser = useCallback(async (): Promise<{ me: User | null } | null> => {
    try {
      const validatedUser = await refetchUserData()
      return validatedUser?.data ?? null
    } catch (error) {
      console.error(error)
      return null
    }
  }, [refetchUserData])

  const loading = loginIsLoading || userIsLoading || emailLoginIsLoading

  const logout = async () => {
    setAppLoading(true)
    await client.resetStore()
    clearUserCache()
    setToken('')
    googleLogout()
    oktaLogout()
    Mixpanel.track({ event: 'FE:logout', data: { framework: 'react' } })
    Mixpanel.reset()
    void devcycleClient.resetUser()
    setAppLoading(false)
    window.location.assign('/login')
  }

  const getToken = useCallback((): string | null => {
    return token
  }, [token])

  return {
    login,
    emailLogin,
    logout,
    token,
    setToken,
    getUser,
    loading,
    getToken
  }
}

function oktaLogout() {
  storage.remove('okta-auth-subdomain')
  storage.remove('okta-cache-storage')
  storage.remove('okta-original-uri-storage')
  storage.remove('okta-shared-transaction-storage')
  storage.remove('okta-token-storage')
  storage.remove('mbue_cache_navigation_data')
  storage.remove('mbue_cache_projects_data')
}
